
import React, { useState, useRef, useEffect } from 'react';
import { Send, User, ChevronLeft, TreePine, Video, Calendar, Clock, Check, X } from 'lucide-react';
import { Conversation, Message, UserProfile, UserRole } from '../types';

interface ChatWindowProps {
  conversation: Conversation;
  currentUser: UserProfile;
  otherUser: { name: string, avatar?: string };
  onSendMessage: (text: string, type?: Message['type'], proposalData?: Message['proposalData']) => void;
  onBack?: () => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ 
  conversation, 
  currentUser, 
  otherUser, 
  onSendMessage,
  onBack 
}) => {
  const [inputText, setInputText] = useState('');
  const [showScheduler, setShowScheduler] = useState(false);
  const [proposalDate, setProposalDate] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim()) {
      onSendMessage(inputText);
      setInputText('');
    }
  };

  const handlePropose = () => {
    if (proposalDate) {
      onSendMessage(
        `I would like to propose an interview for ${new Date(proposalDate).toLocaleString()}`,
        'INTERVIEW_PROPOSAL',
        { time: proposalDate, status: 'PENDING' }
      );
      setShowScheduler(false);
      setProposalDate('');
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center">
          <button onClick={onBack} className="md:hidden p-2 mr-2 hover:bg-slate-200 rounded-full transition-colors">
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="relative">
            {otherUser.avatar ? (
              <img src={otherUser.avatar} className="w-12 h-12 rounded-2xl border border-slate-200 shadow-sm" alt={otherUser.name} />
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center border border-emerald-200">
                <User className="w-6 h-6 text-emerald-600" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full"></div>
          </div>
          <div className="ml-4">
            <h3 className="font-black text-slate-800 tracking-tight">{otherUser.name}</h3>
            <p className="text-xs text-emerald-600 font-bold uppercase tracking-widest flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Active Now
            </p>
          </div>
        </div>
        
        {currentUser.role === UserRole.EMPLOYER && (
          <button 
            onClick={() => setShowScheduler(!showScheduler)}
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-black text-xs hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 active:scale-95"
          >
            <Video className="w-4 h-4" />
            Schedule Interview
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/20">
        {conversation.messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-50 p-12">
            <div className="bg-slate-100 p-6 rounded-[2rem]">
               <TreePine className="w-12 h-12 text-slate-300" />
            </div>
            <p className="text-sm text-slate-500 font-medium">No messages yet. Send a quick hello to get the tree moving!</p>
          </div>
        )}
        {conversation.messages.map((msg) => {
          const isMe = msg.senderId === currentUser.id;
          const isProposal = msg.type === 'INTERVIEW_PROPOSAL';

          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] sm:max-w-[70%] group`}>
                <div className={`rounded-[1.5rem] px-5 py-3 text-sm shadow-sm transition-all ${
                  isMe 
                    ? 'bg-emerald-600 text-white rounded-tr-none' 
                    : 'bg-white text-slate-800 border border-slate-100 rounded-tl-none group-hover:border-emerald-200'
                }`}>
                  {isProposal ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${isMe ? 'bg-white/20' : 'bg-blue-50 text-blue-600'}`}>
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-black text-xs uppercase tracking-widest opacity-80">Interview Proposed</p>
                          <p className="font-bold">{new Date(msg.proposalData!.time).toLocaleString()}</p>
                        </div>
                      </div>
                      {!isMe && msg.proposalData?.status === 'PENDING' && (
                        <div className="flex gap-2 pt-2">
                          <button className="flex-1 bg-emerald-600 text-white py-2 rounded-xl font-black text-[10px] hover:bg-emerald-700 transition-all flex items-center justify-center gap-1">
                            <Check className="w-3 h-3" /> Accept
                          </button>
                          <button className="flex-1 bg-slate-100 text-slate-600 py-2 rounded-xl font-black text-[10px] hover:bg-slate-200 transition-all flex items-center justify-center gap-1">
                            <X className="w-3 h-3" /> Decline
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="leading-relaxed font-medium">{msg.text}</p>
                  )}
                  <p className={`text-[9px] mt-2 font-black uppercase tracking-widest ${isMe ? 'text-emerald-200' : 'text-slate-400'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Scheduler Overlay */}
      {showScheduler && (
        <div className="p-6 bg-blue-50 border-t border-blue-100 animate-in slide-in-from-bottom-2 duration-300">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-black text-blue-800 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" /> Propose Interview Time
            </h4>
            <button onClick={() => setShowScheduler(false)} className="text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-3">
            <input 
              type="datetime-local" 
              className="flex-1 bg-white border border-blue-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold"
              value={proposalDate}
              onChange={(e) => setProposalDate(e.target.value)}
            />
            <button 
              onClick={handlePropose}
              disabled={!proposalDate}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-black text-sm hover:bg-blue-700 disabled:opacity-50 transition-all shadow-lg shadow-blue-200"
            >
              Send Proposal
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="p-6 border-t border-slate-100 bg-white">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none transition-all placeholder:text-slate-400"
          />
          <button 
            type="submit"
            disabled={!inputText.trim()}
            className="bg-emerald-600 text-white p-4 rounded-[1.5rem] hover:bg-emerald-700 disabled:opacity-30 disabled:hover:bg-emerald-600 transition-all shadow-xl shadow-emerald-100 active:scale-95 group"
          >
            <Send className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </form>
    </div>
  );
};
